import { useState } from 'react';
import type { CSSProperties, InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * PasswordInput — a plain `<input>` (type="password" by default) with an inline
 * show/hide eye toggle.
 *
 * Every standard input prop (`value`, `onChange`, `required`, `autoFocus`,
 * `placeholder`, `name`, `id`, `autoComplete`, `disabled`, `className`, …) is
 * forwarded to the `<input>` itself, so an existing
 * `<input className="form-input" type="password" … />` can be swapped for
 * `<PasswordInput className="form-input" … />` without touching the form logic.
 *
 * Layout props (`containerClassName` / `containerStyle`) target the wrapper that
 * positions the eye button; use them when the input used to carry its own flex
 * sizing inline (e.g. ShowDetailPage) so the surrounding grid/flex is unchanged.
 *
 * Icons are inline SVG (no icon library): open eye while masked, slashed eye
 * while visible. The toggle must not steal focus from the field, hence
 * `onMouseDown` + `preventDefault` (mouse) — the button stays keyboard-reachable.
 */
type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  /** Extra class on the wrapper that positions the toggle. */
  containerClassName?: string;
  /** Inline style for the wrapper (layout only — width/margin/flex). */
  containerStyle?: CSSProperties;
};

export default function PasswordInput({
  containerClassName,
  containerStyle,
  className,
  disabled,
  ...rest
}: PasswordInputProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const label = visible ? t('common.hidePassword') : t('common.showPassword');

  return (
    <div
      className={`password-field${containerClassName ? ` ${containerClassName}` : ''}`}
      style={containerStyle}
    >
      <input
        {...rest}
        type={visible ? 'text' : 'password'}
        className={className}
        disabled={disabled}
      />
      <button
        type="button"
        className="password-toggle"
        aria-label={label}
        aria-pressed={visible}
        title={label}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setVisible((v) => !v)}
      >
        {visible ? (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 3l18 18" />
            <path d="M10.58 10.58a3 3 0 0 0 4.24 4.24" />
            <path d="M9.9 5.24A9.7 9.7 0 0 1 12 5c6.5 0 10 7 10 7a17.4 17.4 0 0 1-3.32 4.18" />
            <path d="M6.1 6.1A17.2 17.2 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 4-.85" />
          </svg>
        ) : (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}
